import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { VanchiNo, CreateVanchiNoRequest } from '../../types';
import { vanchiNoService } from '../../services/vanchiNoService';

// VanchiNo state interface
export interface VanchiNoState {
  list: VanchiNo[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: VanchiNoState = {
  list: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchVanchiNos = createAsyncThunk(
  'vanchiNo/fetchVanchiNos',
  async (_, { rejectWithValue }) => {
    try {
      const vanchiNos = await vanchiNoService.getVanchiNos();
      return vanchiNos;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch vanchi numbers');
    }
  }
);

export const createVanchiNo = createAsyncThunk(
  'vanchiNo/createVanchiNo',
  async (data: CreateVanchiNoRequest, { rejectWithValue }) => {
    try {
      const vanchiNo = await vanchiNoService.createVanchiNo(data);
      return vanchiNo;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create vanchi number');
    }
  }
);

export const updateVanchiNo = createAsyncThunk(
  'vanchiNo/updateVanchiNo',
  async ({ id, data }: { id: string; data: Partial<CreateVanchiNoRequest> }, { rejectWithValue }) => {
    try {
      const vanchiNo = await vanchiNoService.updateVanchiNo(id, data);
      return vanchiNo;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update vanchi number');
    }
  }
);


// VanchiNo slice
const vanchiNoSlice = createSlice({
  name: 'vanchiNo',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vanchi numbers
      .addCase(fetchVanchiNos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVanchiNos.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchVanchiNos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create vanchi number
      .addCase(createVanchiNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVanchiNo.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
      })
      .addCase(createVanchiNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update vanchi number
      .addCase(updateVanchiNo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVanchiNo.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(vanchiNo => vanchiNo.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateVanchiNo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
  },
});

export const { clearError, setLoading } = vanchiNoSlice.actions;
export default vanchiNoSlice.reducer;
